/**
 * 이미지 라이트박스 모달.
 *
 * - ESC / 오버레이 클릭 / 닫기 버튼으로 종료
 * - 이미지 2장 이상이면 좌·우 화살표(버튼+키보드) 탐색
 * - 우상단 "N/M" 카운터, 하단 다운로드 버튼
 * - body 스크롤 잠금, 열릴 때 이전 포커스 저장 → 닫힐 때 복원
 *
 * 영구지침 보완: docs/develop/community/post.md (첨부 섹션) — 이미지 뷰어 UX
 */
import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react'
import { API_BASE_URL } from '@/lib/constants'
import type { AttachmentRef } from '@/features/community/publicApi'

interface ImageLightboxProps {
  images: AttachmentRef[]
  initialIndex: number
  onClose: () => void
}

export default function ImageLightbox({ images, initialIndex, onClose }: ImageLightboxProps) {
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(0, initialIndex), Math.max(0, images.length - 1)),
  )
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)

  const hasPrev = index > 0
  const hasNext = index < images.length - 1
  const current = images[index]

  useEffect(() => {
    // 이전 포커스 저장 + 닫기 버튼으로 포커스 이동
    previouslyFocusedRef.current = document.activeElement as HTMLElement
    closeButtonRef.current?.focus()

    // body 스크롤 잠금
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        setIndex((i) => (i > 0 ? i - 1 : i))
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        setIndex((i) => (i < images.length - 1 ? i + 1 : i))
      }
    }
    document.addEventListener('keydown', onKey)

    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      // 이전 포커스 복원
      previouslyFocusedRef.current?.focus?.()
    }
    // onClose / images.length 는 stable 전제 (부모가 key 로 재마운트 제어)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!current) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="이미지 뷰어"
      onClick={onClose}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4"
    >
      {/* 상단 바 */}
      <div
        className="absolute left-0 right-0 top-0 flex items-center justify-between p-4 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-body-sm font-medium">
          {index + 1} <span className="text-white/60">/ {images.length}</span>
        </div>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          aria-label="닫기"
          className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white"
        >
          <X size={20} />
        </button>
      </div>

      {/* 이전 */}
      {hasPrev && (
        <button
          type="button"
          aria-label="이전 이미지"
          onClick={(e) => {
            e.stopPropagation()
            setIndex((i) => i - 1)
          }}
          className="absolute left-4 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white"
        >
          <ChevronLeft size={24} />
        </button>
      )}

      {/* 이미지 */}
      <img
        src={`${API_BASE_URL}/files/${current.fileKey}`}
        alt={current.originalName}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] max-w-[90vw] rounded-md object-contain shadow-2xl"
      />

      {/* 다음 */}
      {hasNext && (
        <button
          type="button"
          aria-label="다음 이미지"
          onClick={(e) => {
            e.stopPropagation()
            setIndex((i) => i + 1)
          }}
          className="absolute right-4 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white"
        >
          <ChevronRight size={24} />
        </button>
      )}

      {/* 하단 — 파일명 + 다운로드 */}
      <div
        className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-3 p-4 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="truncate text-caption text-white/70">{current.originalName}</div>
        <a
          href={`${API_BASE_URL}/files/${current.fileKey}`}
          download={current.originalName}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-caption text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white"
        >
          <Download size={14} /> 다운로드
        </a>
      </div>
    </div>
  )
}
